import 'styles/main.scss';

import * as constants from 'helper/constants';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { BrowserRouter, Route, Redirect } from 'react-router-dom';

import AccountConfirmation from 'modules/accountManagement/AccountConfirmation';
import AccountManagement from 'modules/accountManagement/AccountManagement';
import Consents from 'modules/accountManagement/Consents';
import removeUrlParameter from 'helper/helper';
import Contact from 'modules/accountManagement/Contact';
import EditPersonalDetails from 'modules/personalDetails/EditPersonalDetails';
import ForgotPassword from 'modules/login/ForgotPassword';
import Home from 'modules/Home';
import Loading from 'modules/Loading';
import Login from 'modules/login/Login';
import ResetPassword from 'modules/login/ResetPassword';
import ResetPasswordSuccess from 'modules/login/ResetPasswordSuccess';
import Navigation from 'modules/nav/Navigation';
import PersonalDetails from 'modules/personalDetails/PersonalDetails';
import queryString from 'qs';
import SDKBridgingManager from 'helper/SDKBridgingManager';
import SdkExit from 'modules/signup/SdkExit';
import Signup from 'modules/signup/Signup';
import strings from 'helper/localization/localization';
import MockAPI from 'helper/mockApi'; // for prop-types
import API from './api/api'; // for prop-types

class App extends Component {
  constructor(props) {
    super(props);
    const { query } = this.props;

    // Properties
    this.api = this.props.api;
    this.user = {};
    this.fromLink = query.from;
    this.user.mimiId = query.mimiId;
    this.user.mixpanelId = query.mixpanelId;
    this.user.yearOfBirth = query.yob;

    const fromQuery = this.fromLink ? queryString.parse(this.fromLink.split('?')[1]) : {};
    this.api.token = query.token || fromQuery.token;
    const platform = query.platform || fromQuery.platform;
    const SDKVersion = query.SDKVersion || fromQuery.SDKVersion;

    // State
    this.state = {
      loadingStatus: constants.api.loading,
      userLoggedIn: false,
      tokenLogin: false,
      backToProfileLink: null,
      enteredFromWHT: false,
      exitSdkAfterSignup: !!(platform && SDKVersion),
    };

    if (this.state.exitSdkAfterSignup) {
      this.sdk = new SDKBridgingManager();
      this.sdk.setPlatform(platform);
    }

    // Set language
    if (strings.getAvailableLanguages().includes(navigator.language)) {
      strings.setLanguage(navigator.language);
    } else {
      strings.setLanguage(navigator.language.split('-')[0]);
    }

    // Bindings
    this.fetchUser = this.fetchUser.bind(this);
    this.updateUser = this.updateUser.bind(this);
    this.logoutUser = this.logoutUser.bind(this);
  }

  componentDidMount() {
    // Fetch user
    this.fetchUser();
  }

  async fetchUser() {
    if (this.fromLink) {
      const link = this.fromLink.split('?');
      const query = queryString.parse(link[1]);
      if (this.api.token) {
        query.token = this.api.token;
      }
      if (!this.state.exitSdkAfterSignup) {
        this.setState({ backToProfileLink: `${link[0]}?${queryString.stringify(query)}` });
      }
    }

    if (this.user.mimiId) {
      this.setState({ enteredFromWHT: true });
    }

    const fetchUserDetailsResponse = await this.api.fetchUserDetails();
    if (fetchUserDetailsResponse.status === constants.api.success) {
      this.user = { ...fetchUserDetailsResponse.res.body };
      this.setState({
        loadingStatus: constants.api.success,
        tokenLogin: true,
        userLoggedIn: true,
      });
    } else {
      this.setState({ loadingStatus: fetchUserDetailsResponse.status });
    }
  }

  updateUser(user) {
    const newState = { loadingStatus: constants.api.success, userLoggedIn: true };
    let link;
    this.user = user;

    if (window.location.pathname === '/signup' && this.fromLink) {
      const query = queryString.parse(window.location.search.substring(1));
      const tokenlessURL = removeUrlParameter(query.from, 'token');
      link = `${tokenlessURL}&token=${this.api.token}`;
      newState.backToProfileLink = link;
    }

    this.setState(newState);
  }

  logoutUser() {
    this.user = null;
    this.setState({
      loadingStatus: constants.api.unauthorized,
      userLoggedIn: false,
      tokenLogin: false,
    });
  }

  render() {
    if (this.state.loadingStatus === constants.api.loading) {
      return <Loading />;
    }

    return (
      <BrowserRouter>
        <div>
          <Navigation
            userLoggedIn={this.state.userLoggedIn}
            logout={this.logoutUser}
            tokenLogin={this.state.tokenLogin}
            backToProfileLink={this.state.backToProfileLink}
            enteredFromWHT={this.state.enteredFromWHT}
            exitSdkAfterSignup={this.state.exitSdkAfterSignup}
            fromLink={this.fromLink}
          />
          <Route
            exact
            path="/"
            render={props => <Home user={this.user} loadingStatus={this.state.loadingStatus} {...props} />}
          />
          <PrivateRoute
            exact
            path="/details"
            component={PersonalDetails}
            authenticated={this.state.userLoggedIn}
            componentProps={{ user: this.user }}
          />
          <PrivateRoute
            exact
            path="/account/edit"
            authenticated={this.state.userLoggedIn}
            component={EditPersonalDetails}
            componentProps={{
              api: this.api,
              user: this.user,
              updateUser: this.updateUser,
            }}
          />
          <PrivateRoute
            exact
            path="/account/manage"
            authenticated={this.state.userLoggedIn}
            component={AccountManagement}
            componentProps={{
              api: this.api,
              user: this.user,
              updateUser: this.updateUser,
              logoutUser: this.logoutUser,
              enteredFromWHT: this.state.enteredFromWHT,
              backToProfileLink: this.state.backToProfileLink,
            }}
          />
          <PrivateRoute
            exact
            path="/account/consents"
            authenticated={this.state.userLoggedIn}
            component={Consents}
            componentProps={{
              api: this.api,
              // this.user may be undefined
              // this.user.acceptedContactUsageVersion may be 0, 1, or null
              acceptedContactUsage: Boolean(this.user && this.user.acceptedContactUsageVersion > 0),
              updateUser: this.updateUser,
            }}
          />
          <PrivateRoute
            exact
            path="/sdk-exit"
            authenticated={this.state.userLoggedIn}
            component={SdkExit}
            componentProps={{
              api: this.api,
              fromLink: this.fromLink,
              user: this.user,
              sdk: this.sdk,
            }}
          />
          <Route
            exact
            path="/login"
            render={props => (
              <Login
                api={this.api}
                updateUser={this.updateUser}
                backToProfileLink={this.state.backToProfileLink}
                {...props}
              />
            )}
          />
          <Route exact path="/login/forgot-password" render={props => <ForgotPassword api={this.api} {...props} />} />
          <Route
            exact
            path="/signup"
            render={props => (
              <Signup
                user={this.user}
                api={this.api}
                updateUser={this.updateUser}
                enteredFromWHT={this.state.enteredFromWHT}
                backToProfileLink={this.state.backToProfileLink}
                exitSdkAfterSignup={this.state.exitSdkAfterSignup}
                sdk={this.sdk}
                {...props}
              />
            )}
          />
          <Route exact path="/contact" render={props => <Contact user={this.user} {...props} />} />
          <Route
            exact
            path="/users/accountConfirmation"
            render={props => <AccountConfirmation api={this.api} {...props} />}
          />
          <Route exact path="/users/reset-success" render={() => <ResetPasswordSuccess />} />
          <Route
            exact
            path="/users/reset/:passwordResetToken"
            render={({
              match: {
                params: { passwordResetToken },
              },
            }) => <ResetPassword passwordResetToken={passwordResetToken} api={this.api} />}
          />
        </div>
      </BrowserRouter>
    );
  }
}

App.propTypes = {
  api: PropTypes.oneOfType([PropTypes.instanceOf(API), PropTypes.instanceOf(MockAPI)]).isRequired,
  query: PropTypes.shape({
    from: PropTypes.string,
    mimiId: PropTypes.string,
    mixpanelId: PropTypes.string,
    token: PropTypes.string,
    yob: PropTypes.string,
  }).isRequired,
};

const PrivateRoute = ({ component: RouteComponent, authenticated, componentProps, ...rest }) => (
  <Route
    {...rest}
    render={props => {
      const componentReturned = authenticated ? (
        <RouteComponent {...props} {...componentProps} />
      ) : (
        <Redirect
          to={{
            pathname: '/login',
            state: { from: props.location },
          }}
        />
      );

      return componentReturned;
    }}
  />
);

export default App;



// WEBPACK FOOTER //
// ./src/App.jsx